'use client';

import React from 'react';
import ProcessCard from './ProcessCard';
import SectionBadge from './SectionBadge';

/**
 * ProcessSection Component
 *
 * Renders the "How Our Process Works" section for the Services page.
 * Features:
 * - Section badge and heading at the top, centered
 * - Responsive grid of ProcessCard components, each representing a step
 * - Each step includes a title and a rich description (can include JSX)
 *
 * @returns {JSX.Element} The process section with all steps
 */
export default function ProcessSection() {
  /**
   * Array of process steps to display.
   * Each step contains a title and a description (can be JSX for highlighting).
   */
  const processSteps = [
    {
      title: 'Step 1: Discover & Define',
      description: (
        <>
          We start with{' '}
          <span className="gradient-text">
            a deep dive into your business goals, pain points, and technical landscape.
          </span>{' '}
          This helps us align our services with what truly matters to you.
        </>
      ),
    },
    {
      title: 'Step 2: Design & Strategize',
      description: (
        <>
          We craft tailored blueprints —{' '}
          <span className="gradient-text">from UX mockups to cloud architectures</span> — ensuring
          every element is purpose-built for your needs.
        </>
      ),
    },
    {
      title: 'Step 3: Build & Deploy',
      description: (
        <>
          Our experts{' '}
          <span className="gradient-text">
            implement the solution, managing development, integrations, and infrastructure
          </span>{' '}
          setup with quality and speed.
        </>
      ),
    },
    {
      title: 'Step 4: Manage & Scale',
      description: (
        <>
          We stay involved{' '}
          <span className="gradient-text">
            post-launch, offering monitoring, optimization, and support
          </span>{' '}
          as your business evolves.
        </>
      ),
    },
  ];

  return (
    <section className="w-full py-14 md:py-18 bg-androia-gray-50 px-6 mt-16 md:mt-20">
      <div className="max-w-7xl mx-auto">
        {/* Header: Section badge and heading */}
        <div className="flex flex-col items-center mb-16">
          <SectionBadge text="We have got you covered" />
          <h2 className="gradient-text font-manrope font-semibold text-3xl md:text-4xl lg:text-5xl tracking-[-0.48px] md:tracking-[-0.72px] lg:tracking-[-0.96px] leading-[40px] md:leading-[50px] lg:leading-[60px] text-center mt-4 md:mt-6">
            How Our Process Works
          </h2>
        </div>

        {/* Cards Container: Responsive grid of process steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 lg:gap-[90px] max-w-[956px] mx-auto">
          {processSteps.map((step, index) => (
            <div key={index} className="flex">
              <ProcessCard title={step.title} description={step.description} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
